import { EquipmentAssignmentsService, IEquipmentAssignment } from '@shared/services/EquipmentAssignmentsService';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Notify } from '@shared/ui-native/Notify';
import { Button } from '@shared/ui-react/Button';
import { CollapsiblePanel } from '@shared/ui-react/CollapsiblePanel';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { LoadMorePager } from '@shared/ui-react/LoadMorePager';
import { AssignmentCreateEditDialog } from '@shared/vehicles/assignments/AssignmentCreateEditDialog';
import { VehicleListItem } from '@shared/vehicles/VehicleListItem';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from "react";


@observer
export class MapAssignmentsPanel extends React.Component<{
    vehicles: IVehicle[],
    treeData: ITreeData,
    onClickVehicle: (v: IVehicle) => void,
    onLoadModels: (uuids: string[]) => void,
    readOnly: boolean
}> {
    @observable isLoading = false;
    @observable assignments: IEquipmentAssignment[] = null;
    assignmentsPager = new LoadMorePager<IEquipmentAssignment>((page: number, pageSize: number) => {
        return EquipmentAssignmentsService.fetch(null, page, pageSize);
    });
    @observable expandedUuids: Record<string, boolean> = {};

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    render() {
        if (this.assignments === null || this.props.treeData === null) {
            return <div style={{ height: 300 }} className='overlay-loading'></div>;
        }

        if (this.assignments.length === 0) {
            return <>
                <div className='text-center' style={{ marginTop: 100 }}>{__('js_vehicles.assignments.no_assignments')}</div>
                {!this.props.readOnly && <Button secondary style={{ margin: '15px auto 0' }} onClick={() => this.onEdit(null)} disabled={this.assignmentsPager.isLoading}>{__('js_vehicles.assignments.create_assignment')}</Button>}
            </>
        }

        return (
            <>
                {this.assignments.map(a =>
                    <CollapsiblePanel key={a.uuid} label={(isOpen) => <CollapsiblePanelHeader readOnly={this.props.readOnly} a={a} isExpanded={isOpen} onDelete={this.onDelete}
                        onEdit={this.onEdit} onLoadModels={this.onLoadModels} onClone={this.onClone} />}
                        defaultIsOpen={false} animated={false} isOpen={this.expandedUuids[a.uuid]} onToggle={a => this.loadAssignment(a)}>
                        <div className="flex-column gap-half" style={{ marginLeft: 30, marginTop: -3 }}>
                            <div>
                                <strong>{__('general.files')}: {a.documents.length}</strong>
                                <div>
                                    {a.documents.map(uuid => {
                                        const node = FileTreeService.findNodeByUuid(this.props.treeData, uuid);
                                        return node ? <div>{node.name}</div> : <div>Unknown</div>
                                    })}
                                </div>
                            </div>
                            <div>
                                <strong>{__('tablet.vehicles')}: {a.equipment.length}</strong>
                                <div className="vehicles-list" style={{ marginLeft: -10 }}>
                                    {a.equipment.map(uuid => {
                                        const vehicle = this.props.vehicles?.find(v => v.uuid === uuid);
                                        return vehicle ? <VehicleListItem vehicle={vehicle} onClick={this.props.onClickVehicle} key={vehicle.uuid} /> : <div>Unknown</div>
                                    })}
                                </div>
                            </div>
                        </div>
                    </CollapsiblePanel>
                )}

                {!this.assignmentsPager.hasLoadedEverything &&
                    <Button className='block ik-button-secondary--subtle' onClick={() => this.fetchMoreAssignments(true)} disabled={this.assignmentsPager.isLoading}>{__('general.load_more')}</Button>}

                {!this.props.readOnly && <Button secondary style={{ margin: '15px auto 0' }} onClick={() => this.onEdit(null)} disabled={this.assignmentsPager.isLoading}>{__('js_vehicles.assignments.create_assignment')}</Button>}
            </>
        );
    }

    loadAssignment = (a: IEquipmentAssignment) => {
        if (this.expandedUuids[a.uuid]) {
            delete this.expandedUuids[a.uuid];
        } else {
            this.expandedUuids[a.uuid] = true;
        }
    }

    componentDidMount() {
        this.fetchAssignmentsFromStart();
    }

    fetchAssignmentsFromStart = () => {
        this.assignments = null;
        this.assignmentsPager.reset();
        this.assignmentsPager.pageSize = 100;
        this.fetchMoreAssignments(false);
    }

    fetchMoreAssignments = async (notifyEnd: boolean) => {
        this.isLoading = true;

        setTimeout(async () => {
            this.assignmentsPager.loadMore(notifyEnd)
                .then(assignments => {
                    this.assignments = [...(this.assignments ?? []), ...assignments];
                    this.isLoading = false;
                }).catch(err => {
                    if (err?.message !== 'canceled') {
                        this.isLoading = false;
                        this.assignments = [];
                    }
                });
        });
    };

    onLoadModels = (a: IEquipmentAssignment) => {
        this.props.onLoadModels(a.documents);
    }

    onEdit = (old: IEquipmentAssignment) => {
        AssignmentCreateEditDialog.open(old, null, true, (updated: IEquipmentAssignment) => {
            const existingIndex = this.assignments.findIndex(a => a.uuid == updated.uuid);
            if (existingIndex > -1) {
                this.assignments[existingIndex].name = updated.name;
                this.assignments[existingIndex].equipment = updated.equipment;
                this.assignments[existingIndex].documents = updated.documents;
            } else {
                this.assignments.push(updated);
            }
        })
    }

    onClone = (old: IEquipmentAssignment) => {
        AssignmentCreateEditDialog.clone(old, (created: IEquipmentAssignment) => {
            this.assignments.push(created);
        })
    }

    onDelete = (target: IEquipmentAssignment) => {
        if (!confirm(__('general.confirm_delete', target.name))) return false;

        this.isLoading = true;
        EquipmentAssignmentsService.remove(target.uuid)
            .then(() => {
                this.assignments = this.assignments.filter(a => a.uuid !== target.uuid)
                delete this.expandedUuids[target.uuid];
                Notify.success();
            }).catch(() => {
                Notify.error();
            }).finally(() => {
                this.isLoading = false;
            })
    }
}

const CollapsiblePanelHeader = observer((
    {
        a, isExpanded, onDelete, onLoadModels, onEdit, onClone, readOnly
    }: {
        a: IEquipmentAssignment,
        readOnly: boolean,
        isExpanded: boolean,
        onLoadModels: (a: IEquipmentAssignment) => void,
        onEdit: (a: IEquipmentAssignment) => void,
        onClone: (a: IEquipmentAssignment) => void,
        onDelete: (a: IEquipmentAssignment) => void,
    }) => {

    const Inner = isExpanded ?
        <div>{a.name}</div>
        :
        <div className='flex-column'>
            <div>{a.name}</div>
            <div style={{ color: '#868686' }}>{__('general.files')}: {a.documents.length}, {__('tablet.vehicles')}: {a.equipment.length}</div>
        </div>

    return <div className="flex-row" style={{ display: 'inline-flex', justifyContent: 'space-between', flexGrow: 1, width: '100%' }}>
        {Inner}
        {!readOnly &&
            <Dropdown closeOnBodyClick={true} buttonStyle={{ height: 30 }} stopPropagation className='ik-button-small ik-button-secondary--subtle'>
                <i className='iki iki-more' />
                <ul className='dropdown-list clickable'>
                    {a.documents.length > 0 ? <li onClick={() => onLoadModels(a)}>{__('js_map.assignments.load_models')}</li> : null}
                    <li onClick={() => onEdit(a)}>{__('general.edit')}</li>
                    <li onClick={() => onClone(a)}>{__('general.item.clone')}</li>
                    <li><span style={{ color: '#FF473D' }} onClick={() => onDelete(a)}>{__('general.delete')}</span></li>
                </ul>
            </Dropdown>
        }
    </div>
});
